import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, ArrowRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTHS_IT = [
  'gennaio', 'febbraio', 'marzo', 'aprile', 'maggio', 'giugno',
  'luglio', 'agosto', 'settembre', 'ottobre', 'novembre', 'dicembre',
];

const formatPeriod = (period) => {
  if (!period) return '';
  const [y, m] = period.split('-');
  const idx = parseInt(m, 10) - 1;
  return `${MONTHS_IT[idx] || m} ${y}`;
};

/**
 * Banner ricorrente che ricorda all'admin autorizzato di aggiornare
 * le condizioni di mercato (PUN, PSV, Capacity Market, Dispacciamento)
 * del mese precedente. Resta visibile finché i dati non sono inseriti.
 *
 * Props:
 *   variant: 'dashboard' | 'admin' (sola differenza: CTA e testo)
 */
export const MarketDataReminder = ({ variant = 'dashboard' }) => {
  const { isAdmin } = useAuth();
  const [status, setStatus] = useState({ needs_update: false, missing_period: null });

  useEffect(() => {
    if (!isAdmin) return undefined;
    let cancelled = false;
    const load = async () => {
      try {
        const res = await axios.get(`${API}/market-data/reminder-status`, { withCredentials: true });
        if (!cancelled) setStatus(res.data || { needs_update: false });
      } catch (_e) {
        // silent: se fallisce non mostriamo nulla
      }
    };
    load();
    return () => { cancelled = true; };
  }, [isAdmin]);

  if (!isAdmin) return null;

  return (
    <AnimatePresence>
      {status.needs_update && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          className="mb-6 p-4 rounded-2xl border flex items-start gap-3 bg-amber-50 border-amber-300 text-amber-900"
          data-testid="market-data-reminder"
          role="alert"
        >
          <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5 text-amber-600" />
          <div className="flex-1">
            <p className="font-semibold">
              Aggiorna le condizioni di mercato di {formatPeriod(status.missing_period)}
            </p>
            <p className="text-sm mt-1 text-amber-800">
              Inserisci i valori di <strong>PUN</strong>, <strong>PSV</strong>, <strong>Capacity Market</strong> e <strong>Dispacciamento</strong> per il periodo {status.missing_period}. Questo promemoria resterà visibile finché i dati non saranno salvati.
            </p>
          </div>
          {variant === 'dashboard' && (
            <Link
              to="/admin?tab=market"
              className="self-center inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-amber-600 text-white text-sm font-medium hover:bg-amber-700 transition-colors whitespace-nowrap"
              data-testid="market-data-reminder-cta"
            >
              Vai ai Dati Mercato
              <ArrowRight className="w-4 h-4" />
            </Link>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};
